import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

type Question = {
  id: number;
  question: string;
  options: string[];
  answerIndex: number;
};

type AnswerMap = Record<number, number | null>;
type ReviewMap = Record<number, boolean>;

const questions: Question[] = [
  {
    id: 1,
    question: "Which physical quantity is measured in Newton?",
    options: ["Energy", "Force", "Power", "Pressure"],
    answerIndex: 1,
  },
  {
    id: 2,
    question: "The SI unit of electric current is:",
    options: ["Volt", "Ohm", "Ampere", "Watt"],
    answerIndex: 2,
  },
  {
    id: 3,
    question: "Which gas is released during photosynthesis?",
    options: ["Carbon dioxide", "Oxygen", "Nitrogen", "Hydrogen"],
    answerIndex: 1,
  },
  {
    id: 4,
    question: "Derivative of x² is:",
    options: ["x", "2x", "x³", "2"],
    answerIndex: 1,
  },
  {
    id: 5,
    question: "Atomic number of Carbon is:",
    options: ["4", "6", "8", "12"],
    answerIndex: 1,
  },
  {
    id: 6,
    question: "Which organelle is called the powerhouse of the cell?",
    options: ["Nucleus", "Ribosome", "Mitochondria", "Golgi body"],
    answerIndex: 2,
  },
  {
    id: 7,
    question: "Speed is defined as:",
    options: [
      "Distance × Time",
      "Distance / Time",
      "Time / Distance",
      "Mass × Acceleration",
    ],
    answerIndex: 1,
  },
  {
    id: 8,
    question: "The value of sin 90° is:",
    options: ["0", "1", "1/2", "√3/2"],
    answerIndex: 1,
  },
];

const Quiz: React.FC = () => {
  const { testId } = useParams();
  const [searchParams] = useSearchParams();
  const courseId = searchParams.get("courseId") || "";

  const [started, setStarted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(20 * 60);
  const [answers, setAnswers] = useState<AnswerMap>({});
  const [review, setReview] = useState<ReviewMap>({});
  const [warnings, setWarnings] = useState(0);

  const submittedRef = useRef(false);

  const testTitle = useMemo(() => {
    if (testId === "101") return "NEET Full Syllabus Mock Test - 01";
    if (testId === "102") return "JEE Main Physics Practice Test";
    if (testId === "103") return "JEE Mathematics Algebra Test";
    if (testId === "104") return "Foundation Science Weekly Test";
    if (testId === "105") return "Biology Chapter Test - Cell Unit";
    return `Mock Test ${testId || ""}`;
  }, [testId]);

  const answeredCount = Object.values(answers).filter(
    (value) => value !== null && value !== undefined
  ).length;
  const reviewCount = Object.values(review).filter(Boolean).length;

  const currentQuestion = questions[currentIndex];

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  const submitTest = () => {
    if (submittedRef.current) return;

    submittedRef.current = true;
    setSubmitted(true);

    let correct = 0;
    questions.forEach((question) => {
      if (answers[question.id] === question.answerIndex) {
        correct += 1;
      }
    });

    const wrong = answeredCount - correct;
    const score = correct * 4 - wrong;
    const accuracy =
      answeredCount > 0 ? Math.round((correct / answeredCount) * 100) : 0;
    const attemptId = `${Date.now()}-${testId || "test"}`;

    const result = {
      attemptId,
      testId: testId || "",
      courseId,
      testTitle,
      totalQuestions: questions.length,
      answered: answeredCount,
      review: reviewCount,
      warnings,
      correct,
      wrong,
      score,
      accuracy,
      submittedAt: new Date().toISOString(),
    };

    sessionStorage.setItem(`test_attempt_${attemptId}`, JSON.stringify(result));
    window.location.href = `/student-dashboard/test-result/${attemptId}`;
  };

  useEffect(() => {
    if (!started || submitted) return;

    const timer = window.setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          setTimeout(submitTest, 0);
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [started, submitted, answers, review, warnings]);

  useEffect(() => {
    const block = (event: Event) => {
      if (started && !submittedRef.current) {
        event.preventDefault();
      }
    };

    const onVisibilityChange = () => {
      if (started && !submittedRef.current && document.hidden) {
        setWarnings((prev) => prev + 1);
      }
    };

    document.addEventListener("contextmenu", block);
    document.addEventListener("copy", block);
    document.addEventListener("cut", block);
    document.addEventListener("paste", block);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      document.removeEventListener("contextmenu", block);
      document.removeEventListener("copy", block);
      document.removeEventListener("cut", block);
      document.removeEventListener("paste", block);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [started]);

  const chooseOption = (optionIndex: number) => {
    setAnswers((prev) => ({
      ...prev,
      [currentQuestion.id]: optionIndex,
    }));
  };

  const clearAnswer = () => {
    setAnswers((prev) => ({
      ...prev,
      [currentQuestion.id]: null,
    }));
  };

  const toggleReview = () => {
    setReview((prev) => ({
      ...prev,
      [currentQuestion.id]: !prev[currentQuestion.id],
    }));
  };

  const goNext = () => {
    setCurrentIndex((prev) => Math.min(prev + 1, questions.length - 1));
  };

  const goPrev = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  if (!started) {
    return (
      <div className="min-h-screen bg-[#f3f7ff] p-4 text-[#101827] font-['Inter'] sm:p-6 lg:p-8">
        <main className="mx-auto max-w-[1080px]">
          <button
            onClick={() =>
              (window.location.href = `/student-dashboard/tests${
                courseId ? `?courseId=${courseId}` : ""
              }`)
            }
            className="mb-5 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-black text-slate-700 hover:bg-blue-50"
          >
            ← Back
          </button>

          <section className="overflow-hidden rounded-[28px] bg-white shadow-sm">
            <div className="bg-[#0f3f70] p-6 text-white">
              <p className="text-sm font-black uppercase tracking-[0.2em]">
                Test Instructions
              </p>
              <h1 className="mt-3 text-3xl font-black tracking-[-0.04em]">
                {testTitle}
              </h1>
              <p className="mt-2 text-sm font-semibold text-blue-100">
                Please read all instructions carefully before starting the test.
              </p>
            </div>

            <div className="grid gap-6 p-6 lg:grid-cols-[1fr_320px]">
              <div className="space-y-4">
                <Instruction no="01" title="Timer and auto-submit">
                  Test start hone ke baad timer server time ke according chalega.
                  Time complete hote hi test auto-submit ho jayega.
                </Instruction>
                <Instruction no="02" title="Question navigation">
                  Student question palette se kisi bhi question par jump kar sakta hai.
                  Answered, not answered aur review status clearly dikhega.
                </Instruction>
                <Instruction no="03" title="Secure mode">
                  Right click, copy, paste aur inspect shortcuts block rahenge.
                  Tab switch karne par warning count hoga.
                </Instruction>
                <Instruction no="04" title="Answer saving">
                  Har answer frontend state me save hoga. Backend connect hone ke baad
                  attempt id ke saath auto-save API lagegi.
                </Instruction>
              </div>

              <aside className="rounded-[24px] bg-slate-50 p-5">
                <h2 className="text-xl font-black text-slate-950">
                  Test Summary
                </h2>

                <div className="mt-5 space-y-3">
                  <SummaryRow label="Exam" value={testTitle.includes("NEET") ? "NEET" : testTitle.includes("JEE") ? "JEE" : "Foundation"} />
                  <SummaryRow label="Class" value="Class 11 & 12" />
                  <SummaryRow label="Questions" value={String(questions.length)} />
                  <SummaryRow label="Duration" value="20 minutes" />
                  <SummaryRow label="Total Marks" value={String(questions.length * 4)} />
                </div>

                <button
                  onClick={() => setStarted(true)}
                  className="mt-6 w-full rounded-2xl bg-[#0f3f70] px-5 py-3 text-sm font-black text-white hover:bg-blue-700"
                >
                  I Agree, Start Test
                </button>
              </aside>
            </div>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f3f7ff] text-[#101827] font-['Inter']">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white px-4 py-4 shadow-sm sm:px-6">
        <div className="mx-auto flex max-w-[1500px] flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-[11px] font-black uppercase tracking-[0.22em] text-blue-700">
              Secure Quiz Mode
            </p>
            <h1 className="text-xl font-black text-slate-950">{testTitle}</h1>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <HeaderPill label="Time Left" value={formatTime(secondsLeft)} danger={secondsLeft < 120} />
            <HeaderPill label="Answered" value={`${answeredCount}/${questions.length}`} />
            <HeaderPill label="Review" value={String(reviewCount)} />
            <HeaderPill label="Warnings" value={String(warnings)} danger={warnings > 0} />
            <button
              onClick={() => {
                if (window.confirm("Submit test now?")) {
                  submitTest();
                }
              }}
              className="rounded-2xl bg-red-600 px-5 py-3 text-sm font-black text-white hover:bg-red-700"
            >
              Submit Test
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-[1500px] gap-6 p-4 sm:p-6 lg:grid-cols-[1fr_340px] lg:p-8">
        <section className="rounded-[28px] bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 border-b border-slate-100 pb-5 md:flex-row md:items-start md:justify-between">
            <div>
              <p className="text-sm font-black text-blue-700">
                Question {currentIndex + 1} of {questions.length}
              </p>
              <h2 className="mt-3 text-2xl font-black leading-snug text-slate-950">
                {currentQuestion.question}
              </h2>
            </div>

            <button
              onClick={toggleReview}
              className={`rounded-2xl px-5 py-3 text-sm font-black ${
                review[currentQuestion.id]
                  ? "bg-amber-100 text-amber-700"
                  : "bg-slate-100 text-slate-600 hover:bg-amber-50"
              }`}
            >
              {review[currentQuestion.id] ? "Marked for Review" : "Mark Review"}
            </button>
          </div>

          <div className="mt-6 space-y-3">
            {currentQuestion.options.map((option, index) => {
              const selected = answers[currentQuestion.id] === index;

              return (
                <button
                  key={option}
                  onClick={() => chooseOption(index)}
                  className={`flex w-full items-center gap-4 rounded-[22px] border p-5 text-left transition ${
                    selected
                      ? "border-blue-500 bg-blue-50"
                      : "border-slate-200 bg-white hover:border-blue-200 hover:bg-blue-50/50"
                  }`}
                >
                  <span
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-sm font-black ${
                      selected
                        ? "bg-blue-600 text-white"
                        : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {String.fromCharCode(65 + index)}
                  </span>
                  <span className="text-sm font-bold text-slate-700">
                    {option}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="mt-8 flex flex-wrap justify-between gap-3">
            <button
              onClick={goPrev}
              disabled={currentIndex === 0}
              className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-black text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Previous
            </button>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={clearAnswer}
                className="rounded-2xl bg-slate-100 px-5 py-3 text-sm font-black text-slate-600 hover:bg-slate-200"
              >
                Clear
              </button>
              <button
                onClick={goNext}
                disabled={currentIndex === questions.length - 1}
                className="rounded-2xl bg-[#0f3f70] px-5 py-3 text-sm font-black text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                Save & Next
              </button>
            </div>
          </div>
        </section>

        <aside className="rounded-[28px] bg-white p-6 shadow-sm">
          <h2 className="text-xl font-black text-slate-950">
            Question Palette
          </h2>
          <p className="mt-1 text-sm font-semibold text-slate-500">
            Jump to any question.
          </p>

          <div className="mt-5 grid grid-cols-5 gap-3">
            {questions.map((question, index) => {
              const isCurrent = index === currentIndex;
              const isAnswered =
                answers[question.id] !== null && answers[question.id] !== undefined;
              const isReview = review[question.id];

              return (
                <button
                  key={question.id}
                  onClick={() => setCurrentIndex(index)}
                  className={`h-11 rounded-2xl text-sm font-black transition ${
                    isCurrent
                      ? "bg-blue-600 text-white"
                      : isReview
                      ? "bg-amber-100 text-amber-700"
                      : isAnswered
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-slate-100 text-slate-500"
                  }`}
                >
                  {index + 1}
                </button>
              );
            })}
          </div>

          <div className="mt-6 space-y-3 text-sm font-semibold text-slate-600">
            <Legend color="bg-blue-600" label="Current" />
            <Legend color="bg-emerald-400" label="Answered" />
            <Legend color="bg-amber-400" label="Review" />
            <Legend color="bg-slate-300" label="Not answered" />
          </div>
        </aside>
      </main>
    </div>
  );
};

const Instruction = ({
  no,
  title,
  children,
}: {
  no: string;
  title: string;
  children: React.ReactNode;
}) => {
  return (
    <div className="flex gap-4 rounded-[22px] border border-slate-200 bg-white p-5">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-sm font-black text-blue-700">
        {no}
      </div>
      <div>
        <h3 className="text-base font-black text-slate-950">{title}</h3>
        <p className="mt-2 text-sm font-semibold leading-6 text-slate-500">
          {children}
        </p>
      </div>
    </div>
  );
};

const SummaryRow = ({ label, value }: { label: string; value: string }) => {
  return (
    <div className="flex items-center justify-between rounded-2xl bg-white px-4 py-3">
      <span className="text-sm font-bold text-slate-500">{label}</span>
      <span className="text-sm font-black text-slate-950">{value}</span>
    </div>
  );
};

const HeaderPill = ({
  label,
  value,
  danger,
}: {
  label: string;
  value: string;
  danger?: boolean;
}) => {
  return (
    <div
      className={`rounded-2xl px-4 py-2 ${
        danger ? "bg-red-50 text-red-700" : "bg-blue-50 text-blue-700"
      }`}
    >
      <p className="text-[10px] font-black uppercase tracking-wide">{label}</p>
      <p className="text-sm font-black">{value}</p>
    </div>
  );
};

const Legend = ({ color, label }: { color: string; label: string }) => {
  return (
    <div className="flex items-center gap-3">
      <span className={`h-4 w-4 rounded-full ${color}`} />
      <span>{label}</span>
    </div>
  );
};

export default Quiz;
